import React from "react";
import { Box } from "@mui/material";
import logo from "./Logo.png";
import DOMPurify from 'dompurify';

const formatDate = (d) =>
  new Date(d || Date.now()).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

/**
 * Props
 * - member: { name, address1, address2, city, state, zip }
 * - requestDetails: { subject }  // e.g. 'RE: DRB Application - Roof Replacement (Barrel Tile/Capistrano)'
 * - date: string | Date
 * - content: string (HTML injected from SQL)
 */
const ArchitecturalLetterTemplate = ({
  type,
  member = {},
  requestDetails = {},
  date,
  content = "",
  logoUrl,
}) => {
  const today = formatDate(date);

  const recipientLines = [
    member.name,
    member.address1,
    member.address2,
    [member.city, member.state, member.zip].filter(Boolean).join(", "),
  ].filter(Boolean);

  const subjectLine =
    requestDetails.subject ||
    "";

  return (
    <Box
      id="styled-letter"
      sx={{
        // White paper-like page (no dark mode)
        backgroundColor: "#fff",
        color: "#222",
        // Comfortable “letter” column
        maxWidth: 760,
        mx: "auto",
        px: { xs: 3, sm: 8 },
        py: { xs: 4, sm: 8 },
        // Typography to match your original
        fontFamily: `"Georgia", "Times New Roman", serif`,
        fontSize: "11pt",
        lineHeight: 1.7,
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <style>{`
        #styled-letter p { margin: 0 0 14px 0; font-size: 11pt; }
        #styled-letter strong { font-weight: bold; }
        #styled-letter em { font-style: italic; }
        #styled-letter .footer { margin-top: 80px; }
        #styled-letter .logo { text-align: center; margin-bottom: 24px; }
        #styled-letter .date { margin: 8px 0 16px; }
        #styled-letter .address p { margin: 0; }
        #styled-letter .subject { margin: 18px 0 20px; font-weight: bold; }
      `}</style>

      {/* Logo centered like the screenshot */}
      <Box className="logo">
        <img src={logoUrl || logo} alt="HOA Logo" style={{ height: 56 }} />
      </Box>

      {/* Date */}
      <div className="date">{today}</div>

      {/* Recipient block */}
      <Box className="address" sx={{ mb: 3 }}>
        {recipientLines.map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </Box>

      {/* Subject (RE: …) */}
      {subjectLine ? <div className="subject">{subjectLine}</div> : null}

      {/* Injected body from SQL */}
      <div
        style={{ fontSize: "inherit", lineHeight: "inherit" }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(content || ''),
        }}
      />
    </Box>
  );
};

export default ArchitecturalLetterTemplate;
